import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { FaSearchPlus, FaSearchMinus, FaAdjust, FaGlobe, FaEye, FaVolumeUp, FaLock } from 'react-icons/fa';
import { useSite } from '../context/SiteContext';
import './AccessibilityBar.css';

const AccessibilityBar = () => {
  const {
    languages,
    currentLanguage,
    changeLanguage,
    fontSize,
    changeFontSize,
    highContrast,
    toggleHighContrast,
    config,
    moduleAccess,
  } = useSite();

  const [colorBlindMode, setColorBlindMode] = useState(false);
  const [screenReaderMode, setScreenReaderMode] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [translateReady, setTranslateReady] = useState(false);

  // Inject Google Translate script once
  useEffect(() => {
    if (document.getElementById('google-translate-script')) {
      setTranslateReady(true);
      return;
    }
    // Define callback before loading the script
    window.googleTranslateElementInit = function () {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'en',
          includedLanguages: 'en,hi',
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          autoDisplay: false,
        },
        'google_translate_element'
      );
      setTranslateReady(true);
    };

    const script = document.createElement('script');
    script.id = 'google-translate-script';
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const increaseFontSize = () => {
    if (fontSize < 20) changeFontSize(fontSize + 2);
  };

  const decreaseFontSize = () => {
    if (fontSize > 12) changeFontSize(fontSize - 2);
  };

  const resetFontSize = () => {
    changeFontSize(16);
  };

  const toggleColorBlindMode = () => {
    setColorBlindMode(!colorBlindMode);
    if (!colorBlindMode) {
      document.body.classList.add('colorblind-mode');
    } else {
      document.body.classList.remove('colorblind-mode');
    }
  };

  const toggleScreenReader = () => {
    setScreenReaderMode(!screenReaderMode);
    if (!screenReaderMode) {
      document.body.setAttribute('role', 'application');
      document.body.setAttribute('aria-live', 'polite');
    } else {
      document.body.removeAttribute('role');
      document.body.removeAttribute('aria-live');
    }
  };

  // Switch language using the custom language buttons
  const handleLanguageChange = (langCode) => {
    if (currentLanguage === langCode) return;
    changeLanguage(langCode);
  };

  return (
    <div className={`accessibility-bar ${expanded ? 'expanded' : ''} ${highContrast ? 'high-contrast-mode' : ''} ${colorBlindMode ? 'colorblind-mode' : ''}`}>
      <Container fluid className="accessibility-container">
        <div className="accessibility-wrapper">
          {/* Main Controls */}
          <div className="accessibility-main-controls w-100 d-flex justify-content-between align-items-center">

            {/* Left side controls */}
            <div className="d-flex align-items-center flex-wrap" style={{ gap: '15px' }}>
              <div className="control-group font-size-group">
                <span className="control-label">
                  <i className="fas fa-text-height me-1"></i>
                  Font Size:
                </span>
                <button
                  onClick={decreaseFontSize}
                  className="btn btn-sm btn-accessibility"
                  aria-label="Decrease font size"
                  title="Decrease font size"
                >
                  <FaSearchMinus /> A-
                </button>
                <span className="font-size-display">{fontSize}px</span>
                <button
                  onClick={increaseFontSize}
                  className="btn btn-sm btn-accessibility"
                  aria-label="Increase font size"
                  title="Increase font size"
                >
                  <FaSearchPlus /> A+
                </button>
                <button
                  onClick={resetFontSize}
                  className="btn btn-sm btn-accessibility btn-reset"
                  aria-label="Reset font size"
                  title="Reset font size"
                >
                  Reset
                </button>
              </div>

              {config?.high_contrast_enabled !== false && (
                <div className="control-group contrast-group">
                  <button
                    onClick={toggleHighContrast}
                    className={`btn btn-sm btn-accessibility ${highContrast ? 'active' : ''}`}
                    aria-label="Toggle high contrast"
                    title="Toggle high contrast mode"
                  >
                    <FaAdjust /> {highContrast ? 'Normal' : 'High Contrast'}
                  </button>
                </div>
              )}

              <div className="control-group colorblind-group">
                <button
                  onClick={toggleColorBlindMode}
                  className={`btn btn-sm btn-accessibility ${colorBlindMode ? 'active' : ''}`}
                  aria-label="Toggle color blind mode"
                  title="Toggle color blind friendly mode"
                >
                  <FaEye /> Color Blind
                </button>
              </div>

              <div className="control-group screenreader-group">
                <button
                  onClick={toggleScreenReader}
                  className={`btn btn-sm btn-accessibility ${screenReaderMode ? 'active' : ''}`}
                  aria-label="Toggle screen reader mode"
                  title="Toggle screen reader optimization"
                >
                  <FaVolumeUp /> Reader
                </button>
              </div>
            </div>

            {/* Right side controls: Language and Login */}
            <div className="d-flex align-items-center" style={{ gap: '15px', marginLeft: 'auto' }}>
              {/* Language Switcher UI (Custom Buttons) */}
              <div className="control-group language-group">
                <span className="control-label">
                  <FaGlobe className="me-1" />
                  Language:
                </span>
                <div className="language-toggle notranslate" translate="no">
                  <button
                    onClick={() => handleLanguageChange('en')}
                    className={`lang-btn notranslate ${currentLanguage === 'en' ? 'active' : ''}`}
                    translate="no"
                  >
                    EN
                  </button>
                  <button
                    onClick={() => handleLanguageChange('hi')}
                    className={`lang-btn notranslate ${currentLanguage === 'hi' ? 'active' : ''}`}
                    translate="no"
                  >
                    HI
                  </button>
                </div>
                {/* Hidden Google Translate widget container */}
                <div id="google_translate_element" style={{ display: 'none' }}></div>
              </div>

              {/* Official Login Button - rightmost */}
              <div className="control-group official-login-group">
                {moduleAccess.trade_license ? (
                  <Link
                    to="/official-login"
                    className="btn-official-login"
                    title="Official Login"
                    id="official-login-btn"
                  >
                    <FaLock />
                    <span className="login-text">Official Login</span>
                  </Link>
                ) : (
                  <a
                    href="/admin/"
                    className="btn-official-login"
                    title="Official Login"
                    id="official-login-btn"
                  >
                    <FaLock />
                    <span className="login-text">Official Login</span>
                  </a>
                )}
              </div>

              <button
                onClick={() => setExpanded(!expanded)}
                className="btn btn-sm btn-accessibility toggle-btn"
                aria-label="Toggle accessibility menu"
                title="Show/hide more options"
              >
                <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`}></i>
              </button>
            </div>

          </div>

          {/* Expanded Controls */}
          {expanded && (
            <div className="accessibility-expanded-controls">
              <div className="expanded-section">
                <h6 className="section-title">Accessibility Guide</h6>
                <p className="guide-text">
                  Use keyboard shortcuts: <kbd>Alt+A</kbd> for accessibility menu, <kbd>Alt+S</kbd> for skip to main content
                </p>
              </div>

              <div className="expanded-section">
                <h6 className="section-title">Current Settings</h6>
                <ul className="settings-list">
                  <li>Font Size: <strong>{fontSize}px</strong></li>
                  <li>High Contrast: <strong>{highContrast ? 'On' : 'Off'}</strong></li>
                  <li>Color Blind Mode: <strong>{colorBlindMode ? 'On' : 'Off'}</strong></li>
                  <li>Screen Reader: <strong>{screenReaderMode ? 'Enabled' : 'Disabled'}</strong></li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default AccessibilityBar;

